import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
// import Loadable from 'react-loadable'
import actions from 'redux/actions'
import { reducers } from 'redux/core'
import { connect } from 'redaction'
import { StickyProvider } from 'sb-sticky'
import marketingActions from 'actions/marketing'
import { IntersectionObserverConductor } from 'sb-intersection-observer'
import { compose, links, scrollToElement, constants, sentry, object, formatPrice } from 'common/helpers'

import Page500 from 'pages/500/500'
import injectGlobals from 'decorators/injectGlobals'
import MediaProvider from 'containers/MediaProvider/MediaProvider'

import ModalConductor from 'components/modal/ModalConductor/ModalConductor'
import NotificationsConductor from 'components/NotificationsConductor/NotificationsConductor'
import { RequestLoader } from 'components/_new/ui'

import GlobalHelmet from './GlobalHelmet/GlobalHelmet'
import ZinaChecker from './ZinaChecker/ZinaChecker'
import DummyLandscapeMobile from './DummyLandscapeMobile/DummyLandscapeMobile'
import RequestTokensUpdater from './RequestTokensUpdater/RequestTokensUpdater'
import PaymentProcessBanner from './PaymentProcessBanner/PaymentProcessBanner'
import AppStoreBanner from './AppStoreBanner/AppStoreBanner'
import Modals from './Modals/Modals'

import './App.scss'
// TODO hack for webpack to order css files concatenation
import 'components/buttons/Button/Button.scss'


// const ADAInspector = Loadable({ loader: () => import('./ADAInspector/ADAInspector'), loading: () => Loader })

class App extends Component {

  static propTypes = {
    me: PropTypes.shape({
      isLoggedIn: PropTypes.bool, // @connect
      gender: PropTypes.string, // @connect
    }),
    location: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired,
    isNewSubscriptionPaymentProcessEnabled: PropTypes.bool, // @injectGlobals
    isSubscriptionLongProcessingBannerEnabled: PropTypes.bool, // @injectGlobals
  }

  state = {
    isAppError: false,
    isADAInspectorEnabled: false,
  }

  constructor({ location: { query } }) {
    super()

    const runEnv = object.get(window, '__appConfig__.runEnv')

    if (runEnv !== 'prod' && query.ada) {
      this.isADAInspectorEnabled = true
    }
  }

  static getDerivedStateFromError(error) {
    return {
      isAppError: true,
      error,
    }
  }

  componentDidMount() {
    const {
      isNewSubscriptionPaymentProcessEnabled,
      me: { isLoggedIn, paymentProvider, gender, mixpanelClientUid, email, id },
    } = this.props

    if (isLoggedIn) {
      // reset user context if user reload browser
      actions.models.user.setUserContext({
        userId: id,
        email,
        mixpanelId: mixpanelClientUid,
      })

      const isRecurly = paymentProvider === 'RECURLY'

      // check postponed subscription payment
      if (!isRecurly && isNewSubscriptionPaymentProcessEnabled) {
        actions.models.payment.postponedSubscriptionProcessing()
      }
    }

    window.__debug__.links = links
    window.__debug__.actions = actions
    window.__debug__.reducers = reducers

    // We use these methods in GTM
    window.marketingActions = marketingActions

    document.body.classList.add(`body-${gender}`)
    document.body.classList.add(`body-${isLoggedIn ? 'auth' : 'no-auth'}`)
    window.addEventListener('error', this.handleError, true)

    // Removed old cookies
    actions.misc.cookies.removeItem(constants.cookieNames.reactivateSubscription)
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleError)
  }

  UNSAFE_componentWillReceiveProps(props) {
    const { me: { isLoggedIn: newIsLoggedIn, gender: newGender }, location: { pathname: newPathname } } = props
    const { me: { isLoggedIn, gender }, location: { pathname } } = this.props

    // TODO rework it to use with SSR
    if (newGender !== gender) {
      document.body.classList.toggle('body-female')
      document.body.classList.toggle('body-male')
    }

    if (newIsLoggedIn !== isLoggedIn) {
      document.body.classList.toggle('body-no-auth')
      document.body.classList.toggle('body-auth')
    }

    if (newPathname !== pathname) {
      actions.view.productPage.setOldPathname(pathname)

      this.setState({
        isAppError: false,
      })
    }
  }

  componentDidUpdate(props) {
    const { location: { pathname: oldPathname } } = props
    const { location: { pathname, state: locationState } }  = this.props

    if (pathname !== oldPathname) {
      const isMoveBetweenProfilePages = (
        /profile\//.test(oldPathname)
        && /profile\//.test(pathname)
        && !/incubator/.test(pathname)
      )

      /* ATTN if you want to keep scroll on the new page use object instead of url
      {
        pathname: to,
        state: {
          noScroll: true,
        },
      }
      */
      const noScroll = locationState && locationState.noScroll

      if (!noScroll) {
        // TODO this is ductape to scroll smoothly to other anchor then moving between profile pages
        if (isMoveBetweenProfilePages) {
          scrollToElement('userInfo', true, 0, true)
        }
        else {
          scrollToElement('root')
        }
      }
    }
  }

  componentDidCatch(error, info) {
    let err = (error && error.stack) ? error.stack : error

    console.error(err)

    if (typeof err !== 'string') {
      err = JSON.stringify(err, null, 2)
    }

    // TODO add "info.componentStack" as stack trace to "err"
    sentry.captureException(err)
  }

  handleError = (event) => {
    const { target } = event

    if (target) {
      const message = target.tagName ? `${target.tagName} error` : event.message

      const errorData = {
        src: target.src,
        outerHTML: target.outerHTML,
        error: event.error,
      }

      if (event.filename) {
        errorData.filename = `${event.filename}:${event.lineno}:${event.colno}`
      }

      console.error(message, errorData)

      if (typeof target.src === 'string' && /undefined/.test(target.src)) {
        sentry.captureException(message, {
          extra: errorData,
        })
      }
    }
  }

  render() {
    const { isAppError, error } = this.state
    const {
      children, location: { pathname }, me, gender, subscriptionPrice,
      isSubscriptionLongProcessingBannerEnabled,
    } = this.props

    const isRecurly = me.paymentProvider === 'RECURLY'
    const isPaymentProcessBannerVisible = !isRecurly && isSubscriptionLongProcessingBannerEnabled

    if (isAppError) {
      return (
        <Page500 err={error} />
      )
    }

    return (
      <Fragment>
        {/* GlobalHelmet must be above children */}
        <GlobalHelmet
          gender={gender}
          pathname={pathname}
          subscriptionPrice={subscriptionPrice}
        />
        <MediaProvider>
          {
            isPaymentProcessBannerVisible && (
              <PaymentProcessBanner />
            )
          }
          <RequestTokensUpdater />
          <AppStoreBanner />
          <DummyLandscapeMobile />
          <IntersectionObserverConductor>
            <StickyProvider>
              {/* TODO why do we need key="app" ??? */}
              <div key="app" style={{ height: '100%' }}>
                {children}
              </div>
            </StickyProvider>
            <ModalConductor />
          </IntersectionObserverConductor>
          <RequestLoader />
          <NotificationsConductor />
          {/*
            this.isADAInspectorEnabled && (
              <ADAInspector />
            )
          */}
          <Modals />
          <ZinaChecker />
        </MediaProvider>
      </Fragment>
    )
  }
}

export default compose(
  injectGlobals(({ getFeatureToggle }) => ({
    isNewSubscriptionPaymentProcessEnabled: getFeatureToggle(constants.features.newSubscriptionPaymentProcess),
    isSubscriptionLongProcessingBannerEnabled: getFeatureToggle(constants.features.subscriptionLongProcessingBanner),
  })),
  connect({
    me: 'models.me',
    gender: 'models.me.gender',
    location: 'routing.location',
    subscriptionPrice: (state) => formatPrice(state.models.subscription.price, true),
  })
)(App)
